'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { useAuth } from './AuthProvider';
import type { LiveAnnouncement } from '@/lib/announcements';

const STORAGE_KEY = 'announcement-dismissals';

interface Dismissals {
  forever: number[];
  kept: number[];
}

function readDismissals(): Dismissals {
  if (typeof window === 'undefined') return { forever: [], kept: [] };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { forever: [], kept: [] };
    const p = JSON.parse(raw) as Partial<Dismissals>;
    const nums = (v: unknown) => (Array.isArray(v) ? v.filter((n): n is number => typeof n === 'number') : []);
    return { forever: nums(p.forever), kept: nums(p.kept) };
  } catch {
    return { forever: [], kept: [] };
  }
}

interface AnnouncementsContextValue {
  visible: LiveAnnouncement[];
  keptCount: number;
  hideAndKeep: (id: number) => void;
  hideForever: (id: number) => void;
  restoreKept: () => void;
}

const AnnouncementsContext = createContext<AnnouncementsContextValue>({
  visible: [],
  keptCount: 0,
  hideAndKeep: () => {},
  hideForever: () => {},
  restoreKept: () => {},
});

export function AnnouncementsProvider({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const [all, setAll] = useState<LiveAnnouncement[]>([]);
  const [dismissals, setDismissals] = useState<Dismissals>({ forever: [], kept: [] });
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    setDismissals(readDismissals());
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(dismissals));
    } catch {
      /* almacenamiento no disponible: el descarte solo dura la sesión */
    }
  }, [dismissals, hydrated]);

  useEffect(() => {
    if (loading || !user) {
      setAll([]);
      return;
    }
    let cancelled = false;
    fetch('/api/announcements')
      .then(r => (r.ok ? r.json() : { announcements: [] }))
      .then(data => { if (!cancelled) setAll(data.announcements || []); })
      .catch(() => { if (!cancelled) setAll([]); });
    return () => { cancelled = true; };
  }, [user, loading]);

  const hideAndKeep = useCallback((id: number) => {
    setDismissals(d => (d.kept.includes(id) ? d : { forever: d.forever, kept: [...d.kept, id] }));
  }, []);

  const hideForever = useCallback((id: number) => {
    setDismissals(d => ({
      forever: d.forever.includes(id) ? d.forever : [...d.forever, id],
      kept: d.kept.filter(x => x !== id),
    }));
  }, []);

  const restoreKept = useCallback(() => {
    setDismissals(d => (d.kept.length === 0 ? d : { forever: d.forever, kept: [] }));
  }, []);

  const value = useMemo<AnnouncementsContextValue>(() => {
    const foreverSet = new Set(dismissals.forever);
    const keptSet = new Set(dismissals.kept);
    return {
      visible: all.filter(a => !foreverSet.has(a.id) && !keptSet.has(a.id)),
      keptCount: all.filter(a => keptSet.has(a.id)).length,
      hideAndKeep,
      hideForever,
      restoreKept,
    };
  }, [all, dismissals, hideAndKeep, hideForever, restoreKept]);

  return (
    <AnnouncementsContext.Provider value={value}>
      {children}
    </AnnouncementsContext.Provider>
  );
}

export function useAnnouncements() {
  return useContext(AnnouncementsContext);
}
